package fyi.moko.hotpl.ui.home

import androidx.compose.runtime.Composable

/**
 * Platform-independent bridge for backup export/import file picking.
 *
 * Replaces the Android-only SAF `ActivityResultContracts` (`CreateDocument` /
 * `OpenDocument`) in `HomeScreen` so the same backup flow runs on iOS.
 *
 * - **Export** launches a "save to file" picker; once the user chooses a
 *   destination, [produceJson] is invoked (so JSON is only generated when the
 *   user actually picked a location, not on cancel) and the result is written
 *   there. [onResult] receives a [BackupExportResult].
 * - **Import** launches a "pick a file" picker; [onResult] receives a
 *   [BackupImportResult] — the picked file's text on success, distinct from a
 *   user cancellation or a read failure.
 *
 * Android actual: `ActivityResultContracts` + `ContentResolver`.
 * iOS actual: `UIActivityViewController` (export, temp `.json` file) +
 * `UIDocumentPickerViewController` (import).
 */
interface BackupFilePicker {

    fun exportJson(
        suggestedFileName: String,
        produceJson: suspend () -> String,
        onResult: (BackupExportResult) -> Unit,
    )

    fun importJson(onResult: (BackupImportResult) -> Unit)
}

/** Outcome of a backup export, distinguishing a user cancellation from a real failure. */
sealed interface BackupExportResult {
    /** The JSON was written to the chosen destination. */
    data object Success : BackupExportResult
    /** The user dismissed the picker / share sheet before completing. No snackbar. */
    data object Cancelled : BackupExportResult
    /** A pick happened but writing (or JSON generation) failed. */
    data class Failed(val cause: Throwable? = null) : BackupExportResult
}

/** Outcome of a backup import, distinguishing a user cancellation from a read failure. */
sealed interface BackupImportResult {
    /** A file was picked and its text content read successfully. */
    data class Success(val content: String) : BackupImportResult
    /** The user dismissed the picker before choosing. No snackbar. */
    data object Cancelled : BackupImportResult
    /** A file was picked but reading it failed. */
    data class Failed(val cause: Throwable? = null) : BackupImportResult
}

/** Creates a [BackupFilePicker] bound to the current composition. */
@Composable
expect fun rememberBackupFilePicker(): BackupFilePicker